import { Injectable, inject, signal } from '@angular/core';
import { GoogleGenAI, Type } from "@google/genai";
import { NotificationService } from '../notification';

/**
 * Prioritized list of models for automated fallback:
 * 1. gemini-4-argon: Flagship reasoning & multimodal pro model
 * 2. gemini-3.8-flash: Primary flagship multimodal flash model
 * 3. gemini-3.6-flash: High-performance multimodal flash model
 * 4. gemini-3.5-flash-lite: Fast, high-efficiency flash-lite model
 * 5. gemini-3.1-flash-lite: High throughput, fast, generous quota limits
 * 6. gemini-3-flash-preview: Legacy preview fallback
 */
export const FALLBACK_MODELS: readonly string[] = [
  'gemini-4-argon',
  'gemini-3.8-flash',
  'gemini-3.6-flash',
  'gemini-3.5-flash-lite',
  'gemini-3.1-flash-lite',
  'gemini-3-flash-preview'
] as const;

export interface FallbackEvent {
  failedModel: string;
  nextModel: string;
  reason: string;
  timestamp: Date;
  feature: string;
}

@Injectable({
  providedIn: 'root'
})
export class AiService {
  private notificationService = inject(NotificationService);

  // Current active model index in FALLBACK_MODELS
  private activeModelIndex = signal<number>(0);
  
  // Public signal exposing the currently active model
  readonly currentModel = signal<string>(FALLBACK_MODELS[0]);

  // Set of models that have hit quota limits, with expiration time (30 minutes)
  private quotaExhaustedModels = new Map<string, number>();

  // History of fallback events for debugging and transparency
  readonly fallbackLog = signal<FallbackEvent[]>([]);

  private async getAI() {
    if (typeof GEMINI_API_KEY === 'undefined' || !GEMINI_API_KEY) {
      throw new Error("GEMINI_API_KEY is not defined. Please configure it in the Secrets panel.");
    }
    return new GoogleGenAI({ apiKey: GEMINI_API_KEY });
  }

  /**
   * Helper to inspect if an error is quota-related, rate-limited, or transient model failure
   */
  private isQuotaOrModelError(err: any): boolean {
    if (!err) return false;
    const msg = (err?.message || err?.toString() || '').toLowerCase();
    const status = err?.status || err?.statusCode || (err?.error && err?.error?.code);
    return (
      status === 429 ||
      status === 503 ||
      status === 404 ||
      msg.includes('quota') ||
      msg.includes('daily limit') ||
      msg.includes('rate limit') ||
      msg.includes('resource_exhausted') ||
      msg.includes('resourceexhausted') ||
      msg.includes('exceeded your current quota') ||
      msg.includes('too many requests') ||
      msg.includes('overloaded') ||
      msg.includes('not found') ||
      msg.includes('is not supported')
    );
  }

  /**
   * Safely parses JSON response from model, stripping markdown code fences if present
   */
  private parseModelJsonResponse(text: string | null | undefined): any {
    if (!text) return {};
    let cleaned = text.trim();
    if (cleaned.startsWith('```json')) {
      cleaned = cleaned.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }
    try {
      return JSON.parse(cleaned);
    } catch (e) {
      console.warn('JSON parsing failed on raw model output, returning empty object:', e, text);
      return {};
    }
  }

  /**
   * Cleans up expired quota entries (older than 30 minutes)
   */
  private cleanupExpiredQuotas() {
    const now = Date.now();
    for (const [model, expireTime] of this.quotaExhaustedModels.entries()) {
      if (now > expireTime) {
        this.quotaExhaustedModels.delete(model);
      }
    }
  }

  /**
   * Automatically executes a Gemini operation with fallback across candidate models
   */
  private async executeWithFallback<T>(
    featureName: string,
    operation: (ai: GoogleGenAI, model: string) => Promise<T>
  ): Promise<T> {
    this.cleanupExpiredQuotas();
    const ai = await this.getAI();
    const totalModels = FALLBACK_MODELS.length;

    // Start with the current active model or next available non-exhausted model
    let startIndex = this.activeModelIndex();
    let attempts = 0;
    let lastError: any = null;

    while (attempts < totalModels) {
      const modelIndex = (startIndex + attempts) % totalModels;
      const model = FALLBACK_MODELS[modelIndex];

      // If model is marked as exhausted in this session, skip unless all have failed
      if (this.quotaExhaustedModels.has(model) && attempts < totalModels - 1) {
        attempts++;
        continue;
      }

      try {
        const result = await operation(ai, model);
        
        // If this model succeeded and is different from the previous active model, update active model
        if (this.activeModelIndex() !== modelIndex) {
          this.activeModelIndex.set(modelIndex);
          this.currentModel.set(model);
        }
        return result;

      } catch (err: any) {
        lastError = err;
        const isQuota = this.isQuotaOrModelError(err);
        console.warn(`[AiService] Feature "${featureName}" failed on model "${model}". Quota/Transient error: ${isQuota}. Error:`, err?.message || err);

        if (isQuota) {
          // Mark model as quota exhausted for 30 minutes
          this.quotaExhaustedModels.set(model, Date.now() + 30 * 60 * 1000);
        }

        // Determine next model in fallback chain
        const nextIndex = (modelIndex + 1) % totalModels;
        const nextModel = FALLBACK_MODELS[nextIndex];

        // Record fallback event
        const event: FallbackEvent = {
          failedModel: model,
          nextModel,
          reason: err?.message || 'Quota exceeded or model unavailable',
          timestamp: new Date(),
          feature: featureName
        };
        this.fallbackLog.update(log => [event, ...log.slice(0, 19)]);

        // Notify user about automatic model fallback switch
        this.notificationService.info(
          `AI service reached capacity on ${model}. Automatically switched to backup model (${nextModel}) for ${featureName}.`
        );

        // Advance active model index
        this.activeModelIndex.set(nextIndex);
        this.currentModel.set(nextModel);

        attempts++;
      }
    }

    console.error(`[AiService] All ${totalModels} candidate models failed for feature "${featureName}". Last error:`, lastError);
    this.notificationService.error(
      `AI service quota exceeded across available models. Please try again later.`
    );
    throw lastError;
  }

  getActiveModel(): string {
    return this.currentModel();
  }

  resetFallback(): void {
    this.quotaExhaustedModels.clear();
    this.activeModelIndex.set(0);
    this.currentModel.set(FALLBACK_MODELS[0]);
  }

  async generateFutureVision(title: string, description: string) {
    try {
      return await this.executeWithFallback('Future Vision', async (ai, model) => {
        const response = await ai.models.generateContent({
          model,
          contents: `Based on the following product/service, generate a "Future Vision" (what it will become in 5 years), 3 upcoming milestones with target statuses, a potential market forecast, and a strategy score (0-100).
          
          Title: ${title}
          Description: ${description}
          
          Return as JSON with: 
          - vision (string, max 150 chars)
          - milestones (array of 3 objects with id, label, status: "planned"|"in-progress"|"completed")
          - forecast (string, max 200 chars)
          - strategyScore (number, 0-100)`,
          config: {
            responseMimeType: 'application/json',
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                vision: { type: Type.STRING },
                milestones: { 
                  type: Type.ARRAY,
                  items: { 
                    type: Type.OBJECT,
                    properties: {
                      id: { type: Type.STRING },
                      label: { type: Type.STRING },
                      status: { 
                        type: Type.STRING,
                        enum: ['planned', 'in-progress', 'completed']
                      }
                    },
                    required: ['id', 'label', 'status']
                  }
                },
                forecast: { type: Type.STRING },
                strategyScore: { type: Type.NUMBER }
              },
              required: ['vision', 'milestones', 'forecast', 'strategyScore']
            }
          }
        });

        return this.parseModelJsonResponse(response.text);
      });
    } catch (err) {
      console.error('Future Vision Error after fallback attempts:', err);
      return null;
    }
  }

  async analyzePAN(base64Data: string, mimeType: string) {
    try {
      return await this.executeWithFallback('PAN Document Verification', async (ai, model) => {
        const response = await ai.models.generateContent({
          model,
          contents: [
            { inlineData: { data: base64Data, mimeType } },
            { 
              text: `Analyze this image carefully. Is this a valid Indian Permanent Account Number (PAN) card?
              - If YES: Set isPan: true. Extract Name, Date of Birth (DD/MM/YYYY), and 10-character PAN number. Set reason to "".
              - If NO (e.g. random object, face, blurry photo, Aadhar card, passport, driving license, credit card, table, or invalid document): Set isPan: false, set name/dob/panNumber to empty strings, and set reason to a clear explanation asking the user to align a valid PAN card inside the frame and retake the photo.`
            }
          ],
          config: {
            responseMimeType: 'application/json',
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                isPan: { type: Type.BOOLEAN },
                name: { type: Type.STRING },
                dob: { type: Type.STRING },
                panNumber: { type: Type.STRING },
                reason: { type: Type.STRING },
              },
              required: ['isPan', 'name', 'dob', 'panNumber'],
            }
          }
        });

        return this.parseModelJsonResponse(response.text);
      });
    } catch (err) {
      console.error('PAN Analysis Error after fallback attempts:', err);
      return { isPan: false, name: '', dob: '', panNumber: '', reason: 'Failed to process document image.' };
    }
  }

  async verifySelfie(base64Data: string) {
    try {
      return await this.executeWithFallback('Face Verification Real-Time Monitoring', async (ai, model) => {
        const response = await ai.models.generateContent({
          model,
          contents: [
            { inlineData: { data: base64Data, mimeType: 'image/jpeg' } },
            { 
              text: `Analyze this live selfie camera frame in real-time for Face Verification.
Monitor four stages strictly:
1. "clearLighting" (boolean): Lighting check - Is ambient lighting sufficient and clearly illuminating the face?
2. "faceStraightAndLive" (boolean): Face alignment check - Is a real, live human face present and aligned, looking straight ahead into the camera?
3. "noCapNoMask" (boolean): No face covering check - Is the user's face free of coverings (no caps, hats, face masks, or scarves)?
4. "properlyDressed" (boolean): Dress presence check - Confirming ONLY that clothing is being worn (dress presence).

Return JSON matching the schema.`
            }
          ],
          config: {
            responseMimeType: 'application/json',
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                clearLighting: { type: Type.BOOLEAN },
                faceStraightAndLive: { type: Type.BOOLEAN },
                noCapNoMask: { type: Type.BOOLEAN },
                properlyDressed: { type: Type.BOOLEAN },
              },
              required: ['clearLighting', 'faceStraightAndLive', 'noCapNoMask', 'properlyDressed']
            }
          }
        });

        return this.parseModelJsonResponse(response.text);
      });
    } catch (err) {
      console.error('Selfie Verification Error after fallback attempts:', err);
      return { clearLighting: false, faceStraightAndLive: false, noCapNoMask: false, properlyDressed: false };
    }
  }

  async validateCapturedSelfie(base64Data: string): Promise<{
    isValid: boolean;
    hasHumanFace: boolean;
    clearLighting: boolean;
    faceAligned: boolean;
    noCovering: boolean;
    clothingWorn: boolean;
    reason: string;
  }> {
    try {
      return await this.executeWithFallback('Captured Face Photo Validation', async (ai, model) => {
        const response = await ai.models.generateContent({
          model,
          contents: [
            { inlineData: { data: base64Data, mimeType: 'image/jpeg' } },
            { 
              text: `Perform a rigorous validation on this captured face photo for identity verification.
Strict Requirements:
1. "hasHumanFace" (boolean): Is a clear, genuine human face present in the photo? (Must be FALSE if the photo is of a wrong object, inanimate item, animal, blank wall, photo of photo, illustration, or missing face).
2. "clearLighting" (boolean): Is the illumination clear and adequate with no severe shadows or glare obscuring facial features?
3. "faceAligned" (boolean): Is the face properly aligned and looking forward toward the camera?
4. "noCovering" (boolean): Is the face free from coverings such as caps, hats, sunglasses, or face masks?
5. "clothingWorn" (boolean): Is dress presence confirmed, confirming only that clothing is being worn?
6. "isValid" (boolean): Set to TRUE ONLY IF "hasHumanFace" is TRUE AND all criteria (clearLighting, faceAligned, noCovering, clothingWorn) are fully satisfied. If a wrong object, missing face, face covering, or any failed condition is detected, set isValid to FALSE.
7. "reason" (string): If isValid is false, provide a concise, user-friendly error message explaining the exact issue (for example: "Wrong object detected - please present a real human face.", "No human face detected in the photo.", "Face covering detected - please remove caps or masks.", "Lighting is insufficient or blurry.", "Dress presence not confirmed - please ensure clothing is worn.") and prompt the user to retake the photo. If isValid is true, return empty string "".`
            }
          ],
          config: {
            responseMimeType: 'application/json',
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                isValid: { type: Type.BOOLEAN },
                hasHumanFace: { type: Type.BOOLEAN },
                clearLighting: { type: Type.BOOLEAN },
                faceAligned: { type: Type.BOOLEAN },
                noCovering: { type: Type.BOOLEAN },
                clothingWorn: { type: Type.BOOLEAN },
                reason: { type: Type.STRING },
              },
              required: ['isValid', 'hasHumanFace', 'clearLighting', 'faceAligned', 'noCovering', 'clothingWorn', 'reason']
            }
          }
        });

        const parsed = this.parseModelJsonResponse(response.text);
        return {
          isValid: !!parsed?.isValid,
          hasHumanFace: !!parsed?.hasHumanFace,
          clearLighting: !!parsed?.clearLighting,
          faceAligned: !!parsed?.faceAligned,
          noCovering: !!parsed?.noCovering,
          clothingWorn: !!parsed?.clothingWorn,
          reason: parsed?.reason || (parsed?.isValid ? '' : 'Validation failed: Criteria not fulfilled. Please retake the photo.')
        };
      });
    } catch (err: any) {
      console.error('Captured Photo Validation Error after fallback attempts:', err);
      return {
        isValid: false,
        hasHumanFace: false,
        clearLighting: false,
        faceAligned: false,
        noCovering: false,
        clothingWorn: false,
        reason: 'Unable to validate photo. Please ensure camera is clear and retake the photo.'
      };
    }
  }

  async predictOutcome(title: string, vision: string) {
    try {
      return await this.executeWithFallback('Predict Outcome', async (ai, model) => {
        const response = await ai.models.generateContent({
          model,
          contents: `Given the brand title "${title}" and its 5-year vision "${vision}", predict the market impact and a "success probability" score. 
          Return as JSON: { "impact": "text", "successProbability": number (0-100), "riskFactor": "text" }`,
          config: {
            responseMimeType: 'application/json',
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                impact: { type: Type.STRING },
                successProbability: { type: Type.NUMBER },
                riskFactor: { type: Type.STRING }
              },
              required: ['impact', 'successProbability', 'riskFactor']
            }
          }
        });

        return this.parseModelJsonResponse(response.text);
      });
    } catch (err) {
      console.error('Prediction Error after fallback attempts:', err);
      return null;
    }
  }

  async generateBotReply(
    orgInfo: {
      displayName: string;
      description?: string;
      industry?: string;
      email?: string;
      phoneNumber?: string;
      address?: string;
      products?: any[];
    },
    userMessage: string,
    chatHistory: { sender: 'user' | 'bot'; text: string }[]
  ): Promise<string> {
    try {
      return await this.executeWithFallback('Automated Bot Reply', async (ai, model) => {
        const historyText = chatHistory.slice(-6).map(m => `${(m.sender || 'USER').toUpperCase()}: ${m.text}`).join('\n');
        const productsText = orgInfo.products?.length 
          ? orgInfo.products.map(p => `- ${p.name || p.title || 'Product'}: $${p.price || 0} (${p.description || ''})`).join('\n')
          : 'No catalog items listed';

        const prompt = `You are the official Automated Assistant Bot for "${orgInfo.displayName}".
Organization Information:
- Industry: ${orgInfo.industry || 'Business'}
- About: ${orgInfo.description || 'Professional Organization'}
- Email: ${orgInfo.email || 'N/A'}
- Phone: ${orgInfo.phoneNumber || 'N/A'}
- Address: ${orgInfo.address || 'N/A'}
- Catalog / Products:
${productsText}

Tone & Guidelines:
1. Respond politely in official assistant style with friendly emojis (🟢, 💼, 📦, 🕒, 🏢, etc.).
2. Keep replies concise, helpful, and formatted with clean bullet points or bold text (e.g. *Title*).
3. If asked about products, services, or prices, quote from the catalog items above.
4. Keep the total response under 120 words.

Chat History:
${historyText}

Customer Inquiry: "${userMessage}"

Assistant Bot Response:`;

        const response = await ai.models.generateContent({
          model,
          contents: prompt,
        });

        return response.text?.trim() || `Thank you for contacting ${orgInfo.displayName}! How else can we help you today? 🟢`;
      });
    } catch (err) {
      console.error('Bot reply generation error:', err);
      return `Thank you for messaging ${orgInfo.displayName}! We received your inquiry and our team will get back to you shortly. 🟢`;
    }
  }
}
